import { type RefObject, useEffect } from "react";

interface PopoverElements {
  anchorRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLElement | null>;
}

interface PopoverReactions {
  onClose: () => void;
  place: () => void;
}

/**
 * While open: a pointer down outside the panel and the anchor, or Escape, closes it (Escape also
 * returns the focus to the anchor); resizing, or scrolling anything but the panel, re-places it.
 */
export function usePopoverDismiss(open: boolean, { anchorRef, panelRef }: PopoverElements, { onClose, place }: PopoverReactions): void {
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent): void {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        anchorRef.current?.focus();
      }
    }
    function onScroll(e: Event): void {
      if (panelRef.current?.contains(e.target as Node)) return;
      place();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, onClose, anchorRef, panelRef, place]);
}
